import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch, http } from "@/lib/api";
import { PageHeader, Loading, SectionTitle } from "@/components/common";
import { fmtUsd, fmtNum } from "@/lib/format";
import { toast } from "sonner";
import { Hammer, Info } from "lucide-react";

const SORTS = [
  ["launches", "Most Launches"],
  ["reached_1m", "Most $1M+ Hits"],
  ["watched", "Most Watched"],
];

const QUALITY = { High: "text-emerald-400", Medium: "text-amber-400", Low: "text-rose-400" };

export default function DevTracker() {
  const [sort, setSort] = useState("launches");
  const { data, loading } = useFetch(`/developers?sort=${sort}&limit=50`, [sort]);
  const nav = useNavigate();

  const track = async (d, e) => {
    e.stopPropagation();
    await http.post("/tracked-developers", { kind: "developer", ref_id: d.address, label: d.short, sub: `${d.launches_count} launches` });
    toast.success("Developer added to Proven Dev Watchlist");
  };

  return (
    <div className="fade-in">
      <PageHeader icon={Hammer} title="Developer Intelligence" subtitle="Track deployer wallet history and previous launches. Factual milestone labels only — no developer is called 'successful' without enough data." />

      <div className="mb-4 flex items-center gap-2 rounded-lg border border-slate-800 bg-panel px-4 py-2.5 text-xs text-slate-400">
        <Info className="h-4 w-4 text-amber-400" /> Milestone counts are historical facts (e.g. "4 reached $500K+"), not guarantees of future launches.
      </div>

      <div className="mb-4 flex gap-2">
        {SORTS.map(([v, l]) => (
          <button key={v} onClick={() => setSort(v)} className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${sort === v ? "border-amber-600/50 bg-amber-500/10 text-amber-300" : "border-slate-800 bg-panel text-slate-400"}`}>{l}</button>
        ))}
      </div>

      {loading || !data ? <Loading /> : (
        <div className="pnut-card overflow-hidden">
          <div className="max-h-[70vh] overflow-auto">
            <table className="pnut-table w-full">
              <thead>
                <tr><th>Developer</th><th>Launches</th><th>$500K+</th><th>$1M+</th><th>$5M+</th><th>Avg Init Liq</th><th>Avg Time to Peak</th><th>Data</th><th>Watchers</th><th></th></tr>
              </thead>
              <tbody>
                {data.data.map((d) => (
                  <tr key={d.address} data-testid={`dev-row-${d.short}`} onClick={() => nav(`/developer/${d.address}`)}>
                    <td className="font-mono text-slate-200">{d.short}</td>
                    <td className="font-mono text-slate-300">{d.launches_count}</td>
                    <td className="font-mono text-slate-300">{d.reached_500k}</td>
                    <td className="font-mono text-emerald-400">{d.reached_1m}</td>
                    <td className="font-mono text-emerald-400">{d.reached_5m}</td>
                    <td className="font-mono text-slate-400">{fmtUsd(d.avg_initial_liq)}</td>
                    <td className="font-mono text-slate-400">{d.avg_time_to_peak_min}m</td>
                    <td className={QUALITY[d.data_quality]}>{d.data_quality}</td>
                    <td className="font-mono text-cyan-400">{fmtNum(d.watchers_count)}</td>
                    <td><button onClick={(e) => track(d, e)} className="rounded-md border border-amber-600/40 px-2 py-1 text-[10px] font-semibold text-amber-300 hover:bg-amber-500/10">Follow</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
